import { useCallback, useState } from 'react';

/**
 * Whether engagement figures count people or every recorded open.
 *
 * ── WHY THIS IS A TOGGLE AND NOT A SETTING ───────────────────────────────────
 *
 * Apple Mail Privacy Protection prefetches and caches images, so a recorded "open" may never have
 * involved a human. SendGrid flags those with `sg_machine_open`, and the flag exists ONLY on the
 * webhook — no Stats API response exposes it. So `human` is the default everywhere, because a blended
 * rate for an Apple-heavy audience is roughly "how many recipients use Apple Mail", and a re-send to
 * non-openers built on it mails the wrong people.
 *
 * The other view still has to be reachable, for one concrete reason: every other tool in the world
 * reports the blended figure, so somebody comparing against one needs to see the same number rather
 * than conclude our reporting is broken.
 *
 * ── THE FILTER IS IMPERFECT IN BOTH DIRECTIONS ───────────────────────────────
 *
 * Worth stating wherever this appears, and the UI does: a genuine open by an MPP recipient is ALSO
 * flagged as machine, so for those contacts the open data is unreliable in both directions — the human
 * figure undercounts real readers and the blended one overcounts. Neither is "the truth"; the human
 * one is the safer default because acting on it errs towards mailing fewer people.
 */
export type OpenCounting = 'human' | 'all';

const STORAGE_KEY = 'campaigns.open-counting';

/**
 * Read and validated, not cast. `localStorage` is a boundary like any other and the value there is
 * whatever was last written by any version of this application (security.md § 4).
 */
function readStored(): OpenCounting {
  try {
    const value = localStorage.getItem(STORAGE_KEY);

    return value === 'all' ? 'all' : 'human';
  } catch {
    // Throws in private mode in some browsers. A default is a fine answer.
    return 'human';
  }
}

/**
 * Persisted, because a reader comparing several campaigns against another tool would otherwise reset
 * the control on every navigation — and the reset silently changes what the numbers mean.
 */
export function useOpenCounting(): {
  counting: OpenCounting;
  setCounting: (next: OpenCounting) => void;
  isHuman: boolean;
} {
  const [counting, setState] = useState<OpenCounting>(readStored);

  const setCounting = useCallback((next: OpenCounting) => {
    setState(next);

    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not persisting is a degraded experience, not a failure worth surfacing.
    }
  }, []);

  return { counting, setCounting, isHuman: counting === 'human' };
}
